import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageHeader } from '@/components/shared/PageHeader';
import { TransactionTable } from '@/components/features/transactions/TransactionTable';
import { TransactionFilters } from '@/components/features/transactions/TransactionFilters';
import { AdminWithdrawDialog } from '@/components/features/transactions/AdminWithdrawDialog';
import { RetryTransactionDialog } from '@/components/features/transactions/RetryTransactionDialog';
import { ReturnFundsDialog } from '@/components/features/transactions/ReturnFundsDialog';
import { CollectPoolDialog } from '@/components/features/wallets/CollectPoolDialog';
import { ExportButton } from '@/components/shared/ExportButton';
import { Button } from '@/components/ui/button';
import { useTransactions, usePoolCollectStatus } from '@/lib/hooks';
import { exportTransactionsToCSV } from '@/utils/export';
import type { Transaction, TransactionFilters as TransactionFiltersType } from '@/lib/types';
import {DataTablePagination} from "@/components/shared";

export const Route = createFileRoute('/_auth/_admin/admin/transactions/')({
  component: TransactionsComponent,
});

function TransactionsComponent() {
  const [filters, setFilters] = useState<TransactionFiltersType>({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [retryTransaction, setRetryTransaction] = useState<Transaction | null>(null);
  const [retryOpen, setRetryOpen] = useState(false);
  const [returnTransaction, setReturnTransaction] = useState<Transaction | null>(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);

    const { data, isLoading } = useTransactions({
        ...filters,
        page,
        per_page: perPage,
        from_date: filters.from_date ? new Date(filters.from_date) : undefined,
        to_date: filters.to_date ? new Date(filters.to_date) : undefined,
    });

  const { data: poolStatus } = usePoolCollectStatus();

  const handleRetry = (transaction: Transaction) => {
    setRetryTransaction(transaction);
    setRetryOpen(true);
  };

  const handleReturnFunds = (transaction: Transaction) => {
    setReturnTransaction(transaction);
    setReturnOpen(true);
  };

  const handleExport = () => {
    if (data?.data) {
      exportTransactionsToCSV(data.data);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Транзакции"
        description=""
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCollectOpen(true)}>
              Сбор с пула{poolStatus?.state === 'running' ? ' (идёт...)' : ''}
            </Button>
            <Button onClick={() => setWithdrawOpen(true)}>Вывод</Button>
            <ExportButton onExportCSV={handleExport} disabled={!data?.data} />
          </div>
        }
      />

      <AdminWithdrawDialog open={withdrawOpen} onOpenChange={setWithdrawOpen} />
      <RetryTransactionDialog open={retryOpen} onOpenChange={setRetryOpen} transaction={retryTransaction} />
      <ReturnFundsDialog open={returnOpen} onOpenChange={setReturnOpen} transaction={returnTransaction} />
      <CollectPoolDialog open={collectOpen} onOpenChange={setCollectOpen} />

        <TransactionFilters
            filters={filters}
            onFiltersChange={(newFilters) => setFilters(newFilters as TransactionFiltersType)}
        />

      <TransactionTable
        data={data?.data || []}
        isLoading={isLoading}
        onRetry={handleRetry}
        onReturnFunds={handleReturnFunds}
      />

        {data?.meta && (
            <DataTablePagination
                meta={data.meta}
                onPageChange={setPage}
                onPerPageChange={setPerPage}
            />
        )}
    </div>
  );
}
